import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import "./index.css";
import { AuthProvider, useAuth } from "./auth/AuthProvider";
import { BrandingProvider, useBranding } from "./branding/BrandingProvider";
import { ThemeProvider } from "./theme/ThemeProvider";
import LoginPage from "./pages/LoginPage";
import SetPasswordPage from "./pages/SetPasswordPage";
import AppShell from "./pages/AppShell";
import CustomerHome from "./pages/CustomerHome";
import CustomerAccountPage from "./pages/CustomerAccountPage";
import CustomerPfaPage from "./pages/CustomerPfaPage";
import CustomerContractsPage from "./pages/CustomerContractsPage";
import CatalogPage from "./pages/CatalogPage";
import CartPage from "./pages/CartPage";
import { MyOrdersPage, OrderDetailPage } from "./pages/OrdersPages";
import { SalesKanban } from "./pages/SalesKanban";
import SalesCooperativesPage from "./pages/SalesCooperativesPage";
import SalesNewOrderPage from "./pages/SalesNewOrderPage";
import { LogisticsHome, LogisticsShipmentWizard } from "./pages/LogisticsPages";
import { LoadPlanList, LoadPlanCreate, LoadPlanDetail } from "./pages/LoadPlanPages";
import { SalesDashboard, LogisticsDashboard, AdminDashboard } from "./pages/Dashboards";
import { AdminHomeKpi, AdminAnomaliesPage, AdminOutboxPage } from "./pages/AdminPages";
import { AdminPriceListsPage, AdminPriceListDetailPage } from "./pages/AdminPriceListsPage";
import AdminAuditLogPage from "./pages/AdminAuditLogPage";
import AdminBrandingPage from "./pages/AdminBrandingPage";
import AdminImportCenterPage from "./pages/AdminImportCenterPage";
import { AdminUsersPage, AdminRbacPage, AdminPricingSimulatorPage, AdminLateLoadPlansPage } from "./pages/AdminP2bPages";
import { AdminOrderImportsListPage, AdminOrderImportDetailPage } from "./pages/AdminOrderImportsPage";
import CustomerImportOrderPage from "./pages/CustomerImportOrderPage";
import AdminCustomerEmailIntakePage from "./pages/AdminCustomerEmailIntakePage";
import AdminChangeRequestsPage from "./pages/AdminChangeRequestsPage";
import { AdminEntitiesPage } from "./pages/AdminEntitiesPages";
import AdminIntelligencePage from "./pages/AdminIntelligencePage";
import { AdminAnnouncementsPage } from "./pages/AdminAnnouncementsPage";
import { AdminNotificationsConfigPage } from "./pages/AdminNotificationsConfigPage";
import { AdminCarrierTariffsPage } from "./pages/AdminCarrierTariffsPage";
import { AdminShippingTiersPage } from "./pages/AdminShippingTiersPage";
import { CustomerAnnouncementsPage, CustomerAnnouncementDetailPage } from "./pages/CustomerAnnouncementsPages";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import ProfileVerificationBanner from "./components/ProfileVerificationBanner";

function Protected({ children, roles, allowForcedChange = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div style={{ padding: "3rem", textAlign: "center" }} className="muted">Caricamento…</div>;
  if (!user) {
    // v43.35.9 — preserve the intended deep-link so email CTAs like
    // /customer/orders/{uuid} survive a login roundtrip. LoginPage reads
    // location.state.from and validates it before navigating.
    const from = `${location.pathname || "/"}${location.search || ""}${location.hash || ""}`;
    return <Navigate to="/login" replace state={{ from }} />;
  }
  // Force password change for users flagged after admin creation / reset.
  if (user.must_change_password && !allowForcedChange) {
    return <Navigate to="/change-password" replace />;
  }
  if (roles && !roles.some((r) => (user.roles || []).includes(r))) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function HomeRoute() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  const roles = user.roles || [];
  if (roles.includes("ADMIN")) return <Navigate to="/admin" replace />;
  if (roles.includes("SALES_OPERATOR")) return <Navigate to="/sales" replace />;
  if (roles.includes("LOGISTICS_OPERATOR")) return <Navigate to="/logistics" replace />;
  return <Navigate to="/customer" replace />;
}

function ShellLayout() {
  return (
    <Protected>
      <AppShell>
        <ProfileVerificationBanner />
        <Outlet />
      </AppShell>
    </Protected>
  );
}

function ThemedApp() {
  const { branding } = useBranding();
  return (
    <ThemeProvider branding={branding}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/set-password" element={<SetPasswordPage />} />
          <Route path="/change-password" element={<Protected allowForcedChange={true}><ChangePasswordPage /></Protected>} />
          <Route element={<ShellLayout />}>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/customer" element={<Protected roles={["CUSTOMER"]}><CustomerHome /></Protected>} />
            <Route path="/customer/catalog" element={<Protected roles={["CUSTOMER"]}><CatalogPage /></Protected>} />
            <Route path="/customer/cart" element={<Protected roles={["CUSTOMER"]}><CartPage /></Protected>} />
            <Route path="/customer/orders" element={<Protected roles={["CUSTOMER"]}><MyOrdersPage /></Protected>} />
            <Route path="/customer/orders/:id" element={<Protected roles={["CUSTOMER"]}><OrderDetailPage /></Protected>} />
            <Route path="/customer/import-order" element={<Protected roles={["CUSTOMER"]}><CustomerImportOrderPage /></Protected>} />
            <Route path="/customer/import-order/:id" element={<Protected roles={["CUSTOMER"]}><CustomerImportOrderPage /></Protected>} />
            <Route path="/admin/customers/:customer_id/email-intake" element={<Protected roles={["ADMIN"]}><AdminCustomerEmailIntakePage /></Protected>} />
            <Route path="/admin/change-requests" element={<Protected roles={["ADMIN"]}><AdminChangeRequestsPage /></Protected>} />
            <Route path="/customer/account" element={<Protected roles={["CUSTOMER"]}><CustomerAccountPage /></Protected>} />
            <Route path="/customer/pfa" element={<Protected roles={["CUSTOMER"]}><CustomerPfaPage /></Protected>} />
            <Route path="/customer/contracts" element={<Protected roles={["CUSTOMER"]}><CustomerContractsPage /></Protected>} />
            <Route path="/customer/announcements" element={<Protected roles={["CUSTOMER"]}><CustomerAnnouncementsPage /></Protected>} />
            <Route path="/customer/announcements/:id" element={<Protected roles={["CUSTOMER"]}><CustomerAnnouncementDetailPage /></Protected>} />
            <Route path="/sales" element={<Protected roles={["SALES_OPERATOR", "ADMIN"]}><SalesKanban /></Protected>} />
            <Route path="/sales/new-order" element={<Protected roles={["SALES_OPERATOR", "ADMIN"]}><SalesNewOrderPage /></Protected>} />
            <Route path="/sales/cooperatives" element={<Protected roles={["SALES_OPERATOR", "ADMIN"]}><SalesCooperativesPage /></Protected>} />
            <Route path="/sales/legacy" element={<Protected roles={["SALES_OPERATOR", "ADMIN"]}><SalesDashboard /></Protected>} />
            <Route path="/logistics" element={<Protected roles={["LOGISTICS_OPERATOR", "ADMIN"]}><LogisticsHome /></Protected>} />
            <Route path="/logistics/ship/:orderId" element={<Protected roles={["LOGISTICS_OPERATOR", "ADMIN"]}><LogisticsShipmentWizard /></Protected>} />
            {/* v43.35.10b — staff order detail also reachable by SALES_OPERATOR.
                order.confirmed.office / .agent emails link here so office/sales users
                clicking the CTA no longer get bounced to "/" (which happened when the
                link pointed at the CUSTOMER-only /customer/orders/:id route). */}
            <Route path="/logistics/order/:id" element={<Protected roles={["LOGISTICS_OPERATOR", "SALES_OPERATOR", "ADMIN"]}><OrderDetailPage /></Protected>} />
            <Route path="/logistics/load-plans" element={<Protected roles={["LOGISTICS_OPERATOR", "ADMIN"]}><LoadPlanList /></Protected>} />
            <Route path="/logistics/load-plans/new" element={<Protected roles={["LOGISTICS_OPERATOR", "ADMIN"]}><LoadPlanCreate /></Protected>} />
            <Route path="/logistics/load-plans/:id" element={<Protected roles={["LOGISTICS_OPERATOR", "ADMIN"]}><LoadPlanDetail /></Protected>} />
            <Route path="/admin" element={<Protected roles={["ADMIN"]}><AdminHomeKpi /></Protected>} />
            <Route path="/admin/anomalies" element={<Protected roles={["ADMIN"]}><AdminAnomaliesPage /></Protected>} />
            <Route path="/admin/outbox" element={<Protected roles={["ADMIN"]}><AdminOutboxPage /></Protected>} />
            <Route path="/admin/users" element={<Protected roles={["ADMIN"]}><AdminUsersPage /></Protected>} />
            <Route path="/admin/rbac" element={<Protected roles={["ADMIN"]}><AdminRbacPage /></Protected>} />
            <Route path="/admin/pricing-simulator" element={<Protected roles={["ADMIN"]}><AdminPricingSimulatorPage /></Protected>} />
            <Route path="/admin/load-plans-late" element={<Protected roles={["ADMIN"]}><AdminLateLoadPlansPage /></Protected>} />
            <Route path="/admin/entities" element={<Protected roles={["ADMIN"]}><AdminEntitiesPage /></Protected>} />
            <Route path="/admin/intelligence" element={<Protected roles={["ADMIN"]}><AdminIntelligencePage /></Protected>} />
            <Route path="/admin/audit" element={<Protected roles={["ADMIN"]}><AdminAuditLogPage /></Protected>} />
            <Route path="/admin/branding" element={<Protected roles={["ADMIN"]}><AdminBrandingPage /></Protected>} />
            <Route path="/admin/imports" element={<Protected roles={["ADMIN"]}><AdminImportCenterPage /></Protected>} />
            <Route path="/admin/order-imports" element={<Protected roles={["ADMIN","SALES_OPERATOR"]}><AdminOrderImportsListPage /></Protected>} />
            <Route path="/admin/order-imports/:id" element={<Protected roles={["ADMIN","SALES_OPERATOR","LOGISTICS_OPERATOR"]}><AdminOrderImportDetailPage /></Protected>} />
            <Route path="/admin/announcements" element={<Protected roles={["ADMIN"]}><AdminAnnouncementsPage /></Protected>} />
            <Route path="/admin/notifications-config" element={<Protected roles={["ADMIN"]}><AdminNotificationsConfigPage /></Protected>} />
            <Route path="/admin/carrier-tariffs" element={<Protected roles={["ADMIN"]}><AdminCarrierTariffsPage /></Protected>} />
            <Route path="/admin/price-lists" element={<Protected roles={["ADMIN"]}><AdminPriceListsPage /></Protected>} />
            <Route path="/admin/price-lists/:id" element={<Protected roles={["ADMIN"]}><AdminPriceListDetailPage /></Protected>} />
            <Route path="/admin/shipping-tiers" element={<Protected roles={["ADMIN"]}><AdminShippingTiersPage /></Protected>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <BrandingProvider>
      <AuthProvider>
        <ThemedApp />
      </AuthProvider>
    </BrandingProvider>
  );
}
